import { sendEventMetric, withMetrics } from "@expert/cloudwatch-metrics";
import { getLogger } from "@expert/logging";
import { useVerifiedCustomerStore } from "@expert/sales-tools";

import { useSessionStore } from "@/sdk/sessions/session.store";

import { linkCustomerToLine } from "./api/interactions";
import { getC360Line, metricDimensions } from "./c360InteractionLifecycle";

const logger = getLogger({ module: "c360" });

// +1 and 10 digits from the call, or the 10 digits the expert typed.
const US_MDN = /^(?:\+1)?(\d{10})$/;

/**
 * Links the session's C360 line to the customer the expert confirmed, so the call shows in that customer's C360
 * history. Called when the expert confirms the customer (`onCustomerConfirmed` in the sales context). Uses the
 * confirmed MDN and billing account number, not the caller's number. Never throws.
 */
export async function linkC360Customer(sessionId: string): Promise<void> {
    try {
        // Read before the wait below: ending the session clears them.
        const partner = useSessionStore.getState().getSession(sessionId)?.currentTask?.partner;
        const { customer, hasPrimaryAccountHolder } = useVerifiedCustomerStore
            .getState()
            .getVerifiedCustomer(sessionId);
        const mdn = customer?.mdn && US_MDN.exec(customer.mdn)?.[1];
        // Sales' `accountNumber` is C360's `billing_account_number`.
        const billingAccountNumber = customer?.accountNumber?.trim();
        const line = await getC360Line(sessionId);
        // No line: recording is off or the start failed. Debug only, since it happens on every unrecorded confirm.
        if (!line) {
            logger.debug({ sessionId, c360LinkSkipped: "no_line" }, "C360 customer link skipped");
            return;
        }
        // No metric: its dimensions need a partner.
        if (!partner) {
            logger.info(
                { sessionId, interactionLineId: line.interactionLineId, c360LinkSkipped: "no_partner" },
                "C360 customer link skipped",
            );
            return;
        }
        // Nothing to link by: counted as its own outcome, not as a failure.
        if (!mdn || !billingAccountNumber) {
            // Sales gets the billing account number from the account holder's agreement. No account holder found:
            // nothing to link by, which is normal. Account holder found but no billing account number: missing data.
            // A failed lookup doesn't update hasPrimaryAccountHolder: it keeps the value from the session's last
            // successful lookup, or false if there was none, and the skip is counted by that value.
            const outcome = !mdn
                ? "no_mdn"
                : hasPrimaryAccountHolder
                  ? "account_holder_without_billing_account_number"
                  : "no_account_holder";
            void sendEventMetric("C360LinkCustomer", { ...metricDimensions(partner), outcome });
            logger.info(
                {
                    sessionId,
                    partner,
                    interactionLineId: line.interactionLineId,
                    c360LinkSkipped: outcome,
                    primaryAccountHolderFound: hasPrimaryAccountHolder,
                },
                "C360 customer link skipped",
            );
            return;
        }
        const result = await withMetrics(
            "C360LinkCustomer",
            () =>
                linkCustomerToLine({
                    sessionId,
                    partner,
                    interactionLineId: line.interactionLineId,
                    mdn,
                    billingAccountNumber,
                }),
            { logger, rethrow: false, logResult: false, dimensions: metricDimensions(partner) },
        );
        // withMetrics logs the error without the session ID.
        if (!result) {
            logger.warn({ sessionId, partner, interactionLineId: line.interactionLineId }, "C360 customer link failed");
            return;
        }
        void sendEventMetric("C360LinkCustomer", { ...metricDimensions(partner), outcome: result.outcome });
        logger.info(
            { sessionId, partner, interactionLineId: line.interactionLineId, c360LinkOutcome: result.outcome },
            "C360 customer link",
        );
    } catch (error) {
        logger.warn({ error, sessionId }, "C360 customer link failed");
    }
}
